import React from 'react';
import {
    Box,
    Modal,
    Typography,
    Stack,
    IconButton,
    Card,
    CardContent,
    Avatar,
    Chip,
    Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import WorkIcon from '@mui/icons-material/Work';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BadgeIcon from '@mui/icons-material/Badge';
import { Occupant } from '../../../services/logBookService';
import { useMediaQuery } from '@mui/material';

interface OccupantDetailsModalProps {
    open: boolean;
    onClose: () => void;
    occupants: Occupant[];
    indigentName: string;
    indigentNumber: string;
}

const OccupantDetailsModal: React.FC<OccupantDetailsModalProps> = ({ 
    open, 
    onClose, 
    occupants, 
    indigentName, 
    indigentNumber 
}) => {
    const isMobile = useMediaQuery('(max-width:600px)');

    const style = {
        position: 'absolute' as const,
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        bgcolor: '#fff',
        borderRadius: '16px',
        boxShadow: 24,
        px: isMobile ? 2 : 4,
        py: isMobile ? 3 : 4,
        width: isMobile ? '80%' : '90%',
        maxWidth: isMobile ? 340 : 600,
        maxHeight: isMobile ? '85vh' : '80vh',
        overflow: 'auto',
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={style}>
                <Stack spacing={3}>
                    {/* Header */}
                    <Stack direction='row' justifyContent='space-between' alignItems='center'>
                        <Stack spacing={0.5}>
                            <Typography
                                fontFamily='Poppins, sans-serif'
                                fontWeight={600}
                                fontSize='20px'
                                color='#132F5F'
                            >
                                Occupant Details
                            </Typography>
                            <Typography
                                fontFamily='Poppins, sans-serif'
                                fontSize='14px'
                                color='#666'
                            >
                                {indigentName} ({indigentNumber})
                            </Typography>
                        </Stack>
                        <IconButton onClick={onClose} sx={{ p: 0.5 }}>
                            <CloseIcon sx={{ color: '#132F5F', fontSize: 24 }} />
                        </IconButton>
                    </Stack>

                    <Divider />

                    {/* Occupants List */}
                    <Stack spacing={2}>
                        {occupants.length === 0 ? (
                            <Box textAlign="center" py={4}>
                                <PersonIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
                                <Typography color="text.secondary">
                                    No occupants found
                                </Typography>
                            </Box>
                        ) : (
                            occupants.map((occupant, index) => (
                                <Card
                                    key={occupant.id}
                                    sx={{
                                        borderRadius: '12px',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                        border: '1px solid #E0E0E0'
                                    }}
                                >
                                    <CardContent sx={{ p: 3 }}>
                                        <Stack spacing={2}>
                                            {/* Header with photo and name */}
                                            <Stack direction='row' spacing={2} alignItems='center'>
                                                <Avatar
                                                    src={occupant.photo_path}
                                                    sx={{ 
                                                        width: 56, 
                                                        height: 56,
                                                        bgcolor: '#132F5F'
                                                    }}
                                                >
                                                    {occupant.name?.charAt(0) || 'O'}
                                                </Avatar>
                                                <Stack spacing={0.5}>
                                                    <Typography
                                                        fontFamily='Poppins, sans-serif'
                                                        fontWeight={600}
                                                        fontSize='16px'
                                                        color='#132F5F'
                                                    >
                                                        {occupant.name} {occupant.surname}
                                                    </Typography>
                                                    <Chip
                                                        label={`Occupant ${index + 1}`}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: '#E3F2FD',
                                                            color: '#1976D2',
                                                            fontSize: '12px',
                                                            fontWeight: 500
                                                        }}
                                                    />
                                                </Stack>
                                            </Stack>

                                            {/* Details Grid */}
                                            <Stack spacing={1.5}>
                                                <Stack direction='row' alignItems='center' spacing={1}>
                                                    <BadgeIcon sx={{ color: '#666', fontSize: 18 }} />
                                                    <Typography fontSize='14px' color='#666'>
                                                        Personal ID:
                                                    </Typography>
                                                    <Typography fontSize='14px' fontWeight={500}>
                                                        {occupant.personal_id || '—'}
                                                    </Typography>
                                                </Stack>

                                                {occupant.cell_number && (
                                                    <Stack direction='row' alignItems='center' spacing={1}>
                                                        <PhoneIcon sx={{ color: '#666', fontSize: 18 }} />
                                                        <Typography fontSize='14px' color='#666'>
                                                            Cell Number:
                                                        </Typography>
                                                        <Typography fontSize='14px' fontWeight={500}>
                                                            {occupant.cell_number}
                                                        </Typography>
                                                    </Stack>
                                                )}

                                                {occupant.employer_name && (
                                                    <Stack direction='row' alignItems='center' spacing={1}>
                                                        <WorkIcon sx={{ color: '#666', fontSize: 18 }} />
                                                        <Typography fontSize='14px' color='#666'>
                                                            Employer:
                                                        </Typography>
                                                        <Typography fontSize='14px' fontWeight={500}>
                                                            {occupant.employer_name}
                                                        </Typography>
                                                    </Stack>
                                                )}

                                                {occupant.monthly_income && (
                                                    <Stack direction='row' alignItems='center' spacing={1}>
                                                        <AttachMoneyIcon sx={{ color: '#666', fontSize: 18 }} />
                                                        <Typography fontSize='14px' color='#666'>
                                                            Monthly Income:
                                                        </Typography>
                                                        <Typography fontSize='14px' fontWeight={500}>
                                                            R {occupant.monthly_income}
                                                        </Typography>
                                                    </Stack>
                                                )}
                                            </Stack>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            ))
                        )}
                    </Stack>

                    {/* Summary */}
                    {/* {occupants.length > 0 && (
                        <Box
                            sx={{
                                bgcolor: '#F5F5F5',
                                borderRadius: '8px',
                                p: 2,
                                textAlign: 'center'
                            }}
                        >
                            <Typography fontSize='14px' color='#666'>
                                Total Occupants: <strong>{occupants.length}</strong>
                            </Typography>
                        </Box>
                    )} */}
                </Stack>
            </Box>
        </Modal>
    );
};

export default OccupantDetailsModal; 